import { useNavigation } from '@react-navigation/native'
import { t } from 'i18next'
import { useCallback, useState } from 'react'
import { proxy } from 'valtio'

import * as payment from '../lib/payment'
import { PlaceOrderInput, PlaceOrderOutput } from '../lib/payment'
import { Res } from '../lib/request/make-request'

import { useCartProduct } from './use-cart-product'

export const state = proxy<{ isPaymentOngoing: boolean; isSlotExpired: boolean }>({
  isPaymentOngoing: false,
  isSlotExpired: false,
})

export const setIsPaymentOngoing = (isPaymentStarted: boolean) => {
  state.isPaymentOngoing = isPaymentStarted
}

export const setIsSlotExpired = (isSlotExpired: boolean) => {
  state.isSlotExpired = isSlotExpired
}

export function usePaymentProcess() {
  const [errorMessage, setErrorMessage] = useState('')
  const [orderId, setOrderId] = useState('')

  const { navigate } = useNavigation()
  const { schedule, clinician, product } = useCartProduct()

  const navigateToClinicianCalendar = useCallback(() => {
    if (!clinician?.id || !product?.slug) return

    return navigate('Main', {
      screen: 'HomeTab',
      params: {
        screen: 'ClinicianCalendar',
        params: { clinicianId: clinician.id, productSlug: product.slug, date: schedule?.startTime, timeout: true },
      },
    })
  }, [clinician?.id, product?.slug, navigate, schedule?.startTime])

  const navigateToProduct = useCallback(() => {
    if (!product?.slug) return

    return navigate('Main', {
      screen: 'HomeTab',
      params: {
        screen: 'Product',
        params: { slug: product.slug },
      },
    })
  }, [navigate, product?.slug])

  const navigateBack = () => {
    if (schedule) {
      return navigateToClinicianCalendar()
    }

    return navigateToProduct()
  }

  const processPayment = async (placeOrderData: PlaceOrderInput) => {
    setIsPaymentOngoing(true)
    if (state.isSlotExpired) {
      setIsSlotExpired(false)
      return navigateBack()
    }
    await payment.processPayment({
      placeOrderData,
      onSuccess: (response: Res<PlaceOrderOutput>) => {
        if (response.data?.workflowId) {
          setOrderId(response.data.workflowId)
        }
      },
      onErrorHandling: error => {
        setErrorMessage(error.message)
        setIsPaymentOngoing(false)
        if (error.code && ['SLOT_LOCK_EXPIRED', 'SLOT_LOCK_NOT_FOUND', 'CART_EXPIRED'].includes(error.code)) {
          return navigateBack()
        }
      },
      t,
    })
  }

  return {
    processPayment,
    orderId,
    errorMessage,
    setErrorMessage,
    isPaymentOngoing: state.isPaymentOngoing,
    state: state,
    isSlotExpired: state.isSlotExpired,
    setIsPaymentOngoing,
    setIsSlotExpired,
  }
}
